import { useRouter } from 'next/router';
import { stringify } from 'querystring';
import { useState } from 'react';
import BottomSheet from '~/components/common/bottom-sheet';
import Button from '~/components/common/button';
import Container from '~/components/common/container';
import { Region } from '~/components/home-page/cities-slider';
import { Translate } from '~/i18n';
import { FilterCategory } from '~/interfaces/filters';
import { CloseIcon } from '~/svgs/common';
import {
   expFiltersSelectExpCategoryEvent,
   expFiltersSelectGenderEvent,
   expFiltersSelectLocationEvent,
   expFiltersSelectPriceRangeEvent,
} from '~/utils/mixpanel-events/experience-filters';
import { AdvancedFiltersList } from './filters-list';
import { objectifyFiltersState, MobileHandlerParams, getStateFromRouter } from './handlers';

type MobileFiltersProps = {
   toggleShowCategories: () => void;
   showCategories: boolean;
   limit: number;
   regions: Region[];
   categories: FilterCategory[];
   closeSheet: () => void;
   showSheet: boolean;
};

export const MobileAdvancedFilters = (props: MobileFiltersProps) => {
   const router = useRouter();
   const [state, setState] = useState<Record<string, any>>(getStateFromRouter(router.query));

   const onFilterChange = (params: MobileHandlerParams) => {
      console.log(objectifyFiltersState(state, params));
      setState(prev => objectifyFiltersState(prev, params));
   };

   const applyFilters = () => {
      const { category, location, gender, price, ...searchQuery } = state;
      //tracking selected category
      category && expFiltersSelectExpCategoryEvent(category);
      //tracking selected gender
      gender && expFiltersSelectGenderEvent(gender);
      //tracking selected location
      location && expFiltersSelectLocationEvent(location);
      //tracking selected price range
      price && expFiltersSelectPriceRangeEvent(price?.split(',')[0], price?.split(',')[1]);

      const loc = location || router.query.location || (category ? 'all-cities' : 'search');
      const cats = category?.split(',') || [];

      const search = Object.keys(searchQuery).length ? `?${stringify(searchQuery)}` : '';

      const path =
         router.pathname.includes('/experiences') || cats.length
            ? '/experiences'
            : router.pathname.includes('/events')
            ? '/events'
            : router.pathname.includes('/coaches')
            ? '/coaches'
            : '';

      router.push(
         `/${router.query.lang}/${loc}${path}${cats.length ? `/c/${cats.join('/')}` : ''}${search}`
      );
   };

   const reset = () => setState({ price: '0,50000', location: 'all-cities' });

   const appliedFiltersCount = Object.entries(state).reduce((total, [type, current]) => {
      switch (type) {
         case 'category':
         case 'gender':
         case 'suitable':
            return total + current?.split(',').length || 0;
         case 'price':
            return current === '0,50000' ? total : total + 1;
         case 'offers':
         case 'location':
            return total + 1;
         default:
            return total;
      }
   }, 0);

   return (
      <BottomSheet
         open={props.showSheet}
         toggle={props.closeSheet}
         maxHeight={typeof window !== 'undefined' ? window.innerHeight * 0.9 : 700}
         header={
            <div className="flex">
               <div className="flex-grow text-lg text-center ltr:ml-6 rtl:mr-6">
                  <Translate id="filters:filters" />
               </div>
               <button onClick={props.closeSheet}>
                  <CloseIcon />
               </button>
            </div>
         }
         footer={
            <div className="flex items-center justify-center">
               <Button
                  type="secondary"
                  onClick={reset}
                  className="!py-2 !border-transparent !shadow-none ltr:mr-8 rtl:ml-8">
                  <Translate id="filters:reset" />
               </Button>
               <Button className="!py-2 w-[126px]" onClick={applyFilters}>
                  <Translate id="filters:apply" />{' '}
                  {appliedFiltersCount ? `( ${appliedFiltersCount} )` : ''}
               </Button>
            </div>
         }>
         <Container>
            <AdvancedFiltersList
               {...props}
               mobile
               values={state}
               onItemChange={(name, value) => onFilterChange({ name, value, type: '' })}
               onCategoryChange={({ name, checked, type, childs }) =>
                  onFilterChange({ name, type, value: checked, childs })
               }
            />
         </Container>
      </BottomSheet>
   );
};
